/**
 * Budget Line Item Dialog
 *
 * Modal dialog for creating and editing budget line items
 * Follows exact Figma design specifications from node 3574-8886
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 30px horizontal, 40px vertical
 * - Gap between sections: 40px
 * - Input height: 59px
 * - Label to input gap: 18px
 * - Field containers gap: 14px
 */

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Check, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CurrencyInput from "@/components/ui/currency-input";
import { InputFile } from "@/components/ui/input-file";
import { budgetLineItemSchema, BudgetLineItemFormData } from "./schemas";
import { BudgetLineItemDialogProps } from "@/types/orcamentos.interface";
import { cn } from "@/lib/class-control";

const PAYMENT_METHODS = ["Pix", "TED", "DOC", "Boleto", "Cartão"] as const;
const STATUS_OPTIONS = ["Pago", "Pendente", "Cancelado", "Em Processamento"] as const;

export function BudgetLineItemDialog({
  open,
  mode,
  topicId,
  topicName,
  itemId,
  initialData,
  onClose,
  onSave,
  isSaving = false,
}: BudgetLineItemDialogProps) {
  // Initialize form with react-hook-form + zod validation
  const form = useForm<BudgetLineItemFormData>({
    resolver: zodResolver(budgetLineItemSchema),
    defaultValues: initialData
      ? {
        name: initialData.name,
        paymentDate: "", // TODO: convert from initialData if needed
        paymentMethod: "Pix",
        value: initialData.value,
        status: "Pendente",
        lastApprovedBudget: initialData.lastApprovedBudget,
        attachments: null,
        fiscalDocType: initialData.fiscalDocType,
        fiscalDocNumber: initialData.fiscalDocNumber,
      }
      : {
        name: "",
        paymentDate: "",
        paymentMethod: "Pix",
        value: 0,
        status: "Pendente",
        lastApprovedBudget: 0,
        attachments: null,
        fiscalDocType: null,
        fiscalDocNumber: null,
      },
  });

  // Handle form submission
  const handleSubmit = form.handleSubmit(async (data) => {
    await onSave(data);
    form.reset();
  });

  // Handle dialog close
  const handleClose = () => {
    if (!isSaving) {
      form.reset();
      onClose();
    }
  };

  // Generate item code display (e.g., "1.1.3 - Jpp Sistemas")
  const itemCode = itemId ? itemId : `${topicId}.novo`;
  const dialogTitle = mode === "edit"
    ? `${itemCode} - ${form.watch("name") || initialData?.name || ""}`
    : `Novo Subitem - ${topicName}`;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[964px] bg-[#15203C] border-[#7846FF] rounded-[20px] px-[30px] py-[40px] overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between gap-[533px] mb-[40px]">
          <h2 className="font-['Plus_Jakarta_Sans'] font-bold text-[24px] text-white flex-1">
            {dialogTitle}
          </h2>
          <DialogClose
            onClick={handleClose}
            disabled={isSaving}
            className="flex items-center gap-[10px] text-[#F6F6F6] hover:text-white transition-colors disabled:opacity-50"
          >
            <p className="font-['Plus_Jakarta_Sans'] font-semibold text-[16px]">
              Fechar
            </p>
            <X size={24} />
          </DialogClose>
        </div>

        {/* Divider */}
        <div className="h-px border-t border-gray-700 mb-[40px]" />

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-[40px]">
          {/* Nome do Evento - Full Width */}
          <div className="flex flex-col gap-[18px]">
            <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
              Nome do Evento
            </label>
            <Input
              {...form.register("name")}
              placeholder="Júlio Consultor"
              disabled={isSaving}
              className={cn(
                "bg-[#15203C] border-[#5D5D5D] border-solid h-[59px] rounded-[10px] px-[20px] py-[10px]",
                "text-[#D1D1D1] text-[14px] font-['Plus_Jakarta_Sans'] font-normal",
                "focus-within:ring-2 focus-within:ring-[#7846FF]",
                form.formState.errors.name && "border-red-500"
              )}
            />
            {form.formState.errors.name && (
              <p className="text-red-500 text-sm">{form.formState.errors.name.message}</p>
            )}
          </div>

          {/* Data de pagamento & Forma de Pagamento - Two Columns */}
          <div className="flex gap-[14px]">
            {/* Data de pagamento */}
            <div className="flex-1 flex flex-col gap-[18px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
                Data de pagamento
              </label>
              <Input
                {...form.register("paymentDate")}
                type="date"
                placeholder="dd/mm/aaaa"
                disabled={isSaving}
                className={cn(
                  "bg-[#15203C] border-[#5D5D5D] border-solid h-[59px] rounded-[10px] px-[20px] py-[10px]",
                  "text-[#D1D1D1] text-[14px] font-['Plus_Jakarta_Sans'] font-normal",
                  "focus-within:ring-2 focus-within:ring-[#7846FF]",
                  form.formState.errors.paymentDate && "border-red-500"
                )}
              />
              {form.formState.errors.paymentDate && (
                <p className="text-red-500 text-sm">{form.formState.errors.paymentDate.message}</p>
              )}
            </div>

            {/* Forma de Pagamento */}
            <div className="flex-1 flex flex-col gap-[18px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
                Forma de Pagamento
              </label>
              <Controller
                name="paymentMethod"
                control={form.control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isSaving}
                  >
                    <SelectTrigger
                      className={cn(
                        "bg-[#15203C] border-[#5D5D5D] border-solid h-[59px] rounded-[10px] px-[20px] py-[10px]",
                        "text-[#D1D1D1] text-[14px] font-['Plus_Jakarta_Sans'] font-normal",
                        "focus-within:ring-2 focus-within:ring-[#7846FF]"
                      )}
                    >
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem key={method} value={method}>
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.paymentMethod && (
                <p className="text-red-500 text-sm">{form.formState.errors.paymentMethod.message}</p>
              )}
            </div>
          </div>

          {/* Valor & Status - Two Columns */}
          <div className="flex gap-[14px]">
            {/* Valor */}
            <div className="flex-1 flex flex-col gap-[18px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
                Valor
              </label>
              {/* <Controller
                name="value"
                control={form.control}
                render={({ field }) => (
                  <CurrencyInput
                    field={field}
                    placeholder="R$ 0,00"
                    disabled={isSaving}
                    className="h-[59px]"
                  />
                )}
              /> */}
              {form.formState.errors.value && (
                <p className="text-red-500 text-sm">{form.formState.errors.value.message}</p>
              )}
            </div>

            {/* Status */}
            <div className="flex-1 flex flex-col gap-[18px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
                Status
              </label>
              <Controller
                name="status"
                control={form.control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isSaving}
                  >
                    <SelectTrigger
                      className={cn(
                        "bg-[#15203C] border-[#5D5D5D] border-solid h-[59px] rounded-[10px] px-[20px] py-[10px]",
                        "text-[#D1D1D1] text-[14px] font-['Plus_Jakarta_Sans'] font-normal",
                        "focus-within:ring-2 focus-within:ring-[#7846FF]"
                      )}
                    >
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.status && (
                <p className="text-red-500 text-sm">{form.formState.errors.status.message}</p>
              )}
            </div>
          </div>

          {/* Último orçamento aprovado - Full Width */}
          <div className="flex flex-col gap-[18px]">
            <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
              Orçamento Previsto
            </label>
            {/* <Controller
              name="lastApprovedBudget"
              control={form.control}
              render={({ field }) => (
                <CurrencyInput
                  field={field}
                  placeholder="R$ 0,00"
                  disabled={isSaving}
                  className="h-[59px]"
                />
              )}
            /> */}
            {form.formState.errors.lastApprovedBudget && (
              <p className="text-red-500 text-sm">{form.formState.errors.lastApprovedBudget.message}</p>
            )}
          </div>

          {/* Anexar comprovante - Full Width */}
          <div className="flex flex-col gap-[18px]">
            <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white">
              Anexar comprovante, Nota Fiscal
            </label>
            <Controller
              name="attachments"
              control={form.control}
              render={({ field }) => (
                <div
                  className={cn(
                    "bg-[#15203C] border-[#5D5D5D] border-solid flex items-center gap-[22px] h-[59px] px-[20px] py-[10px] rounded-[10px]",
                    "focus-within:ring-2 focus-within:ring-[#7846FF]"
                  )}
                >
                  <Upload size={24} className="text-gray-400" />
                  <InputFile
                    field={field}
                    placeholder="Faça upload (JPG, PDF, PNG)"
                  />
                </div>
              )}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-[20px] justify-end">
            <Button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className={cn(
                "bg-[#DC4426] hover:bg-[#c23a1f] h-[50px] w-[149px] rounded-[10px]",
                "flex items-center justify-center gap-[3px]",
                "font-['Plus_Jakarta_Sans'] font-bold text-[18px] text-[#F5F2FF]",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className={cn(
                "bg-[#13D480] hover:bg-[#10b56d] h-[50px] w-[149px] rounded-[10px]",
                "flex items-center justify-center gap-[3px]",
                "font-['Plus_Jakarta_Sans'] font-bold text-[18px] text-[#F5F2FF]",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <Check size={24} />
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
